import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUserContext } from '../../contexts';
import { continuePortalLoginFn, teamFn } from '../../lib/api';
import { setCookie } from '../../lib/utils';
import {
    clearPortalLoginContinuation,
    getPortalLoginContinuation,
    openPortalWithRefreshToken,
} from '../../lib/portalLogin';
import '../../styles/layout.scss';
import './ManageSeats.scss';
import { Header } from '../Header';
import { Spinner } from '../Spinner';

const DESKTOP_SEAT_CONTEXT = 'desktop';
const POLLING_INTERVAL_MS = 10000;
const TOAST_DURATION_MS = 4000;

const parseSeatMetric = (value, fallback = 0) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

const coerceBoolean = (value, fallback = true) => {
    if (value === undefined || value === null) {
        return fallback;
    }

    if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();
        if (['false', '0', 'no'].includes(normalizedValue)) {
            return false;
        }
        if (['true', '1', 'yes'].includes(normalizedValue)) {
            return true;
        }
    }

    return Boolean(value);
};

const getNormalizedManagementScope = (value, fallback = 'self') => {
    if (value === 'team' || value === 'self') {
        return value;
    }

    return fallback;
};

const getDesktopPayloadSource = (rawPayload) => {
    if (!rawPayload || typeof rawPayload !== 'object') {
        return null;
    }

    if (rawPayload.desktop && typeof rawPayload.desktop === 'object') {
        return {
            ...rawPayload.desktop,
            management_scope: rawPayload.desktop.management_scope ?? rawPayload.management_scope,
            can_revoke_seats: rawPayload.desktop.can_revoke_seats ?? rawPayload.can_revoke_seats,
            seat_context: rawPayload.desktop.seat_context ?? rawPayload.seat_context ?? DESKTOP_SEAT_CONTEXT,
        };
    }

    return rawPayload;
};

const normalizeDesktopSeatsPayload = (rawPayload, fallbackScope) => {
    const payloadSource = getDesktopPayloadSource(rawPayload);
    if (!payloadSource || typeof payloadSource !== 'object') {
        return null;
    }

    const seats = Array.isArray(payloadSource.seats)
        ? payloadSource.seats.map((seat) => ({
            ...seat,
            seat_context: seat?.seat_context || payloadSource.seat_context || DESKTOP_SEAT_CONTEXT,
            device_label: seat?.device_label || seat?.device_name || seat?.device || 'Desktop device',
        }))
        : [];

    const seatsTotal = parseSeatMetric(payloadSource.seats_total, seats.length);
    const seatsUsed = parseSeatMetric(payloadSource.seats_used, seats.length);
    const seatsAvailable = payloadSource.seats_available === null || payloadSource.seats_available === undefined
        ? Math.max(0, seatsTotal - seatsUsed)
        : parseSeatMetric(payloadSource.seats_available);

    return {
        seatContext: payloadSource.seat_context || DESKTOP_SEAT_CONTEXT,
        managementScope: getNormalizedManagementScope(payloadSource.management_scope, fallbackScope),
        canRevokeSeats: coerceBoolean(payloadSource.can_revoke_seats, true),
        seatsTotal,
        seatsUsed,
        seatsAvailable,
        seats,
    };
};

const getSeatTimestampMs = (value) => {
    if (!value) {
        return null;
    }

    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
};

const formatSeatDateTime = (value) => {
    const timestamp = getSeatTimestampMs(value);
    if (!timestamp) {
        return null;
    }

    return new Date(timestamp).toLocaleString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatSeatCountdown = (remainingMs) => {
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
        return '00:00:00';
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');

    if (days > 0) {
        return `${days}d ${hh}:${mm}:${ss}`;
    }

    return `${hh}:${mm}:${ss}`;
};

const getSeatRemainingMs = (seat, nowMs) => {
    const expiresMs = getSeatTimestampMs(seat?.expires_at);
    if (!expiresMs) {
        return null;
    }

    return Math.max(0, expiresMs - nowMs);
};

const getSeatCountdownPercent = (seat, nowMs) => {
    const expiresMs = getSeatTimestampMs(seat?.expires_at);
    if (!expiresMs) {
        return 0;
    }

    const remainingMs = Math.max(0, expiresMs - nowMs);
    const startTimestamp =
        getSeatTimestampMs(seat?.started_at) ??
        getSeatTimestampMs(seat?.issued_at) ??
        getSeatTimestampMs(seat?.created_at) ??
        getSeatTimestampMs(seat?.last_used_at);

    if (startTimestamp && startTimestamp < expiresMs) {
        const totalMs = expiresMs - startTimestamp;
        if (totalMs <= 0) {
            return 0;
        }

        return Math.max(0, Math.min(100, (remainingMs / totalMs) * 100));
    }

    return remainingMs > 0 ? 100 : 0;
};

const getSeatStartTimestamp = (seat) => (
    seat?.started_at ||
    seat?.issued_at ||
    seat?.created_at ||
    null
);

const getSeatDisplayName = (seat) => {
    const firstName = seat?.user?.first_name;
    const lastName = seat?.user?.last_name;
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    if (fullName) {
        return fullName;
    }

    if (seat?.user?.email) {
        return seat.user.email;
    }

    if (seat?.user?.id) {
        return `User #${seat.user.id}`;
    }

    return 'Unknown user';
};

const getDeviceFamily = (seat) => {
    const source = [
        seat?.device_label,
        seat?.device_name,
        seat?.device,
        seat?.os,
        seat?.platform,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    if (/windows|win11|win10|surface/.test(source)) {
        return { key: 'windows', label: 'Windows desktop' };
    }

    if (/macbook|mac mini|mac studio|imac|macos|mac os|os x|osx|mac\b/.test(source)) {
        return { key: 'mac', label: 'macOS desktop' };
    }

    if (/iphone|ipad|ios|android|phone|mobile|galaxy|pixel|tablet/.test(source)) {
        return { key: 'mobile', label: 'Mobile device' };
    }

    if (/linux|ubuntu|debian|fedora|arch/.test(source)) {
        return { key: 'linux', label: 'Linux desktop' };
    }

    return { key: 'desktop', label: 'Desktop device' };
};

const getSeatsErrorMessage = (statusCode, defaultMessage, managementScope) => {
    if (statusCode === 401) {
        return 'Your session expired. Please sign in again.';
    }

    if (statusCode === 403) {
        return managementScope === 'team'
            ? 'You do not have permission to manage these desktop seats.'
            : 'Desktop self-service seat release is not available for this account.';
    }

    if (statusCode === 404) {
        return 'Desktop seat data is not available right now.';
    }

    return defaultMessage || 'Unable to load desktop seats.';
};

const SeatDeviceIcon = ({ familyKey }) => {
    if (familyKey === 'windows') {
        return (
            <svg viewBox="0 0 64 64" aria-hidden="true">
                <path d="M8 10.5 29 7v23H8zm24 19.5V6.5L56 4v26zM8 33h21v23.5L8 53zm24 0h24v27l-24-3.5z" />
            </svg>
        );
    }

    if (familyKey === 'mac') {
        return (
            <svg viewBox="0 0 64 64" aria-hidden="true">
                <path d="M20 15.5h24a8 8 0 0 1 8 8v14a8 8 0 0 1-8 8H20a8 8 0 0 1-8-8v-14a8 8 0 0 1 8-8Zm-4 34h32l3 6H13zM31 21.5a4 4 0 0 0 4-4c0-1.8-.7-3.5-2.2-5-2.3 1.3-3.8 3.3-3.8 5.4 0 2 1 3.6 2 3.6Z" />
            </svg>
        );
    }

    if (familyKey === 'mobile') {
        return (
            <svg viewBox="0 0 64 64" aria-hidden="true">
                <path d="M22 8h20a6 6 0 0 1 6 6v36a6 6 0 0 1-6 6H22a6 6 0 0 1-6-6V14a6 6 0 0 1 6-6Zm2 8v28h16V16Zm8 33a2.4 2.4 0 1 0 0 4.8 2.4 2.4 0 0 0 0-4.8Z" />
            </svg>
        );
    }

    if (familyKey === 'linux') {
        return (
            <svg viewBox="0 0 64 64" aria-hidden="true">
                <path d="M32 8c7 0 11 6.5 11 16 0 4.3-.8 8.2-2 11.5l9 13.5a4.5 4.5 0 0 1-3.8 7H17.8A4.5 4.5 0 0 1 14 49l9-13.5A34 34 0 0 1 21 24c0-9.5 4-16 11-16Zm-4.5 15.5a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6Zm9 0a2.8 2.8 0 1 0 0 5.6 2.8 2.8 0 0 0 0-5.6ZM32 31c-4.6 0-8 2.3-8 5s3.4 5 8 5 8-2.3 8-5-3.4-5-8-5Z" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 64 64" aria-hidden="true">
            <path d="M12 15h40a6 6 0 0 1 6 6v22a6 6 0 0 1-6 6H12a6 6 0 0 1-6-6V21a6 6 0 0 1 6-6Zm2 8v18h36V23ZM24 53h16l2.5 5H21.5z" />
        </svg>
    );
};

const ManageSeats = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticating } = useUserContext();

    const mountedRef = useRef(true);
    const pollingIntervalRef = useRef(null);
    const toastTimeoutRef = useRef(null);
    const seatsPayloadRef = useRef(null);

    const [desktopSeats, setDesktopSeats] = useState(null);
    const [loadingSeats, setLoadingSeats] = useState(false);
    const [seatsError, setSeatsError] = useState(null);
    const [revokingSeatIds, setRevokingSeatIds] = useState({});
    const [toastMessage, setToastMessage] = useState(null);
    const [tickerMs, setTickerMs] = useState(() => Date.now());
    const [portalContinuation, setPortalContinuation] = useState(() => getPortalLoginContinuation());
    const [showPortalContinueModal, setShowPortalContinueModal] = useState(false);
    const [continuingPortalLogin, setContinuingPortalLogin] = useState(false);
    const [portalFallbackUrl, setPortalFallbackUrl] = useState(null);

    const reasonNoSeats = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        return searchParams.get('reason') === 'no-seats';
    }, [location.search]);

    const fallbackManagementScope = user?.is_team_owner ? 'team' : 'self';
    const portalProviderLabel = portalContinuation?.provider === 'apple' ? 'Apple' : 'Google';

    useEffect(() => {
        seatsPayloadRef.current = desktopSeats;
    }, [desktopSeats]);

    useEffect(() => () => {
        mountedRef.current = false;

        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
    }, []);

    const showToast = useCallback((message) => {
        if (!mountedRef.current) {
            return;
        }

        setToastMessage(message);

        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }

        toastTimeoutRef.current = setTimeout(() => {
            if (!mountedRef.current) {
                return;
            }

            setToastMessage(null);
            toastTimeoutRef.current = null;
        }, TOAST_DURATION_MS);
    }, []);

    const preserveReturnPathAndLogin = useCallback(() => {
        const returnPath = `${location.pathname}${location.search}${location.hash || ''}`;
        setCookie('crateLoginRedirect', returnPath, 1);
        navigate('/login', { replace: true });
    }, [location.hash, location.pathname, location.search, navigate]);

    const fetchDesktopSeats = useCallback(async ({ showLoading = false } = {}) => {
        if (!user) {
            return;
        }

        if (showLoading && mountedRef.current) {
            setLoadingSeats(true);
        }

        if (mountedRef.current) {
            setSeatsError(null);
        }

        try {
            const response = await teamFn.seats(DESKTOP_SEAT_CONTEXT);
            if (!mountedRef.current) {
                return;
            }

            const normalizedPayload = normalizeDesktopSeatsPayload(response, fallbackManagementScope);
            if (!normalizedPayload) {
                setSeatsError('Unable to load desktop seats.');
                if (!seatsPayloadRef.current) {
                    setDesktopSeats(null);
                }
                return;
            }

            setDesktopSeats(normalizedPayload);
        } catch (error) {
            if (!mountedRef.current) {
                return;
            }

            const statusCode = error?.response?.status;
            if (statusCode === 401) {
                preserveReturnPathAndLogin();
                return;
            }

            const message = getSeatsErrorMessage(
                statusCode,
                error?.response?.data?.message || error?.message,
                fallbackManagementScope
            );

            setSeatsError(message);
            if (!seatsPayloadRef.current) {
                setDesktopSeats(null);
            }
        } finally {
            if (showLoading && mountedRef.current) {
                setLoadingSeats(false);
            }
        }
    }, [fallbackManagementScope, preserveReturnPathAndLogin, user]);

    useEffect(() => {
        if (isAuthenticating) {
            return;
        }

        if (!user) {
            preserveReturnPathAndLogin();
        }
    }, [isAuthenticating, preserveReturnPathAndLogin, user]);

    useEffect(() => {
        if (!user) {
            return;
        }

        fetchDesktopSeats({ showLoading: true });

        const startPolling = () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }

            if (document.hidden) {
                return;
            }

            pollingIntervalRef.current = setInterval(() => {
                fetchDesktopSeats();
            }, POLLING_INTERVAL_MS);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
                return;
            }

            fetchDesktopSeats();
            startPolling();
        };

        startPolling();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [fetchDesktopSeats, user]);

    useEffect(() => {
        if (!desktopSeats?.seats?.some((seat) => !!seat?.expires_at)) {
            return;
        }

        let tickerIntervalId = null;
        const updateTicker = () => {
            setTickerMs(Date.now());
        };

        const startTicker = () => {
            if (tickerIntervalId) {
                clearInterval(tickerIntervalId);
            }

            if (document.hidden) {
                return;
            }

            updateTicker();
            tickerIntervalId = setInterval(updateTicker, 1000);
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (tickerIntervalId) {
                    clearInterval(tickerIntervalId);
                    tickerIntervalId = null;
                }
                return;
            }

            startTicker();
        };

        startTicker();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (tickerIntervalId) {
                clearInterval(tickerIntervalId);
            }
        };
    }, [desktopSeats]);

    const handleRetry = useCallback(() => {
        fetchDesktopSeats({ showLoading: true });
    }, [fetchDesktopSeats]);

    const handleRevokeSeat = useCallback(async (seat) => {
        const seatId = seat?.seat_id;
        if (!seatId || !desktopSeats?.canRevokeSeats) {
            return;
        }

        const deviceLabel = seat?.device_label || 'this device';
        const confirmMessage = desktopSeats.managementScope === 'team'
            ? `Release ${deviceLabel} for ${getSeatDisplayName(seat)}?`
            : `Release ${deviceLabel}?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setRevokingSeatIds((currentState) => ({
            ...currentState,
            [seatId]: true,
        }));

        try {
            const response = await teamFn.revokeSeat(seatId);
            if (portalContinuation) {
                setShowPortalContinueModal(true);
                showToast('Seat released. Continue your Portal sign-in when you are ready.');
            } else {
                showToast(response?.message || 'Seat released. Return to the desktop app and sign in again.');
            }
            await fetchDesktopSeats();
        } catch (error) {
            const statusCode = error?.response?.status;

            if (statusCode === 401) {
                preserveReturnPathAndLogin();
                return;
            }

            if (statusCode === 403) {
                setDesktopSeats((currentState) => (
                    currentState
                        ? { ...currentState, canRevokeSeats: false }
                        : currentState
                ));
            }

            if (statusCode === 404) {
                showToast('That seat is no longer active.');
                await fetchDesktopSeats();
            } else {
                showToast(error?.response?.data?.message || error?.message || 'Unable to release this seat.');
            }
        } finally {
            if (!mountedRef.current) {
                return;
            }

            setRevokingSeatIds((currentState) => {
                const nextState = { ...currentState };
                delete nextState[seatId];
                return nextState;
            });
        }
    }, [desktopSeats, fetchDesktopSeats, portalContinuation, preserveReturnPathAndLogin, showToast]);

    const handleContinuePortalLogin = useCallback(async () => {
        if (!portalContinuation || continuingPortalLogin) {
            return;
        }

        setContinuingPortalLogin(true);
        try {
            const response = await continuePortalLoginFn();
            if (!response?.refresh_token) {
                throw new Error('Unable to continue the Portal sign-in.');
            }

            clearPortalLoginContinuation();
            setPortalContinuation(null);
            setShowPortalContinueModal(false);
            openPortalWithRefreshToken(response.refresh_token, setPortalFallbackUrl);
        } catch (error) {
            const statusCode = error?.response?.status;
            if (statusCode === 409) {
                showToast('That seat was claimed by another sign-in. Release another seat to continue.');
                await fetchDesktopSeats();
                return;
            }

            if (statusCode === 401 || statusCode === 403) {
                clearPortalLoginContinuation();
                setPortalContinuation(null);
                setShowPortalContinueModal(false);
                showToast('This Portal sign-in expired. Return to Portal and try again.');
                return;
            }

            showToast(error?.response?.data?.message || error?.message || 'Unable to continue the Portal sign-in.');
        } finally {
            if (mountedRef.current) {
                setContinuingPortalLogin(false);
            }
        }
    }, [continuingPortalLogin, fetchDesktopSeats, portalContinuation, showToast]);

    const sortedSeats = useMemo(() => {
        if (!Array.isArray(desktopSeats?.seats)) {
            return [];
        }

        return [...desktopSeats.seats].sort((leftSeat, rightSeat) => {
            const leftTimestamp = getSeatTimestampMs(leftSeat?.last_used_at) ?? 0;
            const rightTimestamp = getSeatTimestampMs(rightSeat?.last_used_at) ?? 0;
            return rightTimestamp - leftTimestamp;
        });
    }, [desktopSeats?.seats]);

    const managementScope = desktopSeats?.managementScope || fallbackManagementScope;
    const hasActiveSeats = sortedSeats.length > 0;
    const hasFreeSeat = parseSeatMetric(desktopSeats?.seatsAvailable, 0) > 0;
    const availabilityBadgeTone = !desktopSeats ? 'muted' : hasFreeSeat ? 'ok' : 'warning';
    const availabilityBadgeLabel = !desktopSeats
        ? 'Checking seat availability'
        : hasFreeSeat
            ? 'A seat is available now'
            : 'No free desktop seats';
    const heroTitle = managementScope === 'team'
        ? (reasonNoSeats ? 'All desktop seats are currently in use' : 'Manage desktop seats')
        : (reasonNoSeats ? 'Sign out another desktop to continue' : 'Manage your desktop seats');
    const heroBody = managementScope === 'team'
        ? 'Release a desktop seat for anyone on the team so the blocked user can sign in again from the app.'
        : 'Release one of your active desktop devices, then go back to the desktop app and sign in again.';
    const scopeLabel = managementScope === 'team' ? 'Team owner access' : 'Self-service access';

    if (isAuthenticating || (!user && !isAuthenticating)) {
        return (
            <div className="layout manage-seats-layout">
                <Header />
                <div className="empty-content">
                    <Spinner />
                </div>
            </div>
        );
    }

    return (
        <div className="layout manage-seats-layout">
            <Header />
            <div className="manage-seats-page">
                <div className="manage-seats-shell">
                    <section className="manage-seats-hero">
                        <div className="manage-seats-hero-copy">
                            <div className="manage-seats-eyebrow">Desktop quick release</div>
                            <h1>{heroTitle}</h1>
                            <p>{heroBody}</p>
                            {portalContinuation && (
                                <div className="manage-seats-portal-identity">
                                    Managing seats for {user?.email} with {portalProviderLabel}
                                </div>
                            )}
                            <div className="manage-seats-badges">
                                <div className="manage-seats-badge">{scopeLabel}</div>
                                <div className={`manage-seats-badge ${availabilityBadgeTone}`}>
                                    {availabilityBadgeLabel}
                                </div>
                                {desktopSeats && !desktopSeats.canRevokeSeats && (
                                    <div className="manage-seats-badge muted">Seat release disabled</div>
                                )}
                            </div>
                            <ol className="manage-seats-steps">
                                <li>Choose a desktop session below.</li>
                                <li>Release the seat you want to free up.</li>
                                <li>Return to the app and sign in again.</li>
                            </ol>
                        </div>

                        <aside className="manage-seats-summary">
                            <div className="summary-panel">
                                <div className="summary-label">Seat context</div>
                                <div className="summary-value">Desktop</div>
                            </div>
                            <div className="summary-grid">
                                <div className="summary-panel">
                                    <div className="summary-label">Active</div>
                                    <div className="summary-value">{desktopSeats?.seatsUsed ?? '--'}</div>
                                </div>
                                <div className="summary-panel">
                                    <div className="summary-label">Available</div>
                                    <div className={`summary-value ${desktopSeats && hasFreeSeat ? 'ok' : ''}`}>
                                        {desktopSeats?.seatsAvailable ?? '--'}
                                    </div>
                                </div>
                                <div className="summary-panel">
                                    <div className="summary-label">Total</div>
                                    <div className="summary-value">{desktopSeats?.seatsTotal ?? '--'}</div>
                                </div>
                                <div className="summary-panel">
                                    <div className="summary-label">Refresh</div>
                                    <div className="summary-value">10s</div>
                                </div>
                            </div>
                        </aside>
                    </section>

                    {toastMessage && (
                        <div className="manage-seats-toast" role="status">
                            {toastMessage}
                        </div>
                    )}

                    {portalContinuation && !showPortalContinueModal && (
                        <div className="manage-seats-state portal-continue">
                            <span>A Portal sign-in is ready to continue.</span>
                            <button type="button" className="manage-seats-inline-btn" onClick={() => setShowPortalContinueModal(true)}>
                                Continue with {portalProviderLabel}
                            </button>
                        </div>
                    )}

                    {portalFallbackUrl && (
                        <div className="manage-seats-state portal-continue">
                            <span>Portal did not open automatically.</span>
                            <a className="manage-seats-inline-btn" href={portalFallbackUrl}>Open ProductionCrate Portal</a>
                        </div>
                    )}

                    {seatsError && (
                        <div className="manage-seats-state error">
                            <span>{seatsError}</span>
                            <button type="button" className="manage-seats-inline-btn" onClick={handleRetry}>
                                Retry
                            </button>
                        </div>
                    )}

                    {desktopSeats && !desktopSeats.canRevokeSeats && (
                        <div className="manage-seats-state warning">
                            <span>
                                {managementScope === 'team'
                                    ? 'Your current permissions do not allow releasing desktop seats.'
                                    : 'Seat release is disabled for this account. Contact your team owner if you still cannot sign in.'}
                            </span>
                        </div>
                    )}

                    {loadingSeats && !desktopSeats && (
                        <div className="manage-seats-loading-grid" aria-hidden="true">
                            {[0, 1, 2].map((item) => (
                                <div className="loading-card" key={`loading-seat-card-${item}`}>
                                    <div className="loading-line large"></div>
                                    <div className="loading-line"></div>
                                    <div className="loading-line"></div>
                                    <div className="loading-line medium"></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {desktopSeats && (
                        <section className="manage-seats-content">
                            <div className="manage-seats-content-head">
                                <div>
                                    <div className="section-title">Active desktop seats</div>
                                    <div className="section-copy">
                                        {managementScope === 'team'
                                            ? 'These are the desktop sessions currently occupying seats across the team.'
                                            : 'These are the desktop sessions currently using your seat allocation.'}
                                    </div>
                                </div>
                                <a className="manage-seats-account-link" href="/account">
                                    Open full account
                                </a>
                            </div>

                            {!hasActiveSeats && (
                                <div className="manage-seats-empty">
                                    <div className="empty-title">No active desktop seats right now</div>
                                    <div className="empty-copy">
                                        Return to the desktop app and try signing in again.
                                    </div>
                                </div>
                            )}

                            {hasActiveSeats && (
                                <div className="manage-seats-grid">
                                    {sortedSeats.map((seat) => {
                                        const family = getDeviceFamily(seat);
                                        const seatId = seat?.seat_id;
                                        const isRevoking = !!revokingSeatIds[seatId];
                                        const remainingMs = getSeatRemainingMs(seat, tickerMs);
                                        const hasExpiration = remainingMs !== null;
                                        const isExpired = hasExpiration && remainingMs <= 0;
                                        const countdownPercent = getSeatCountdownPercent(seat, tickerMs);
                                        const sessionStart = formatSeatDateTime(getSeatStartTimestamp(seat));
                                        const lastUsed = formatSeatDateTime(seat?.last_used_at);
                                        const expiresAt = formatSeatDateTime(seat?.expires_at);

                                        return (
                                            <article
                                                className={`seat-device-card ${family.key}`}
                                                key={`desktop-seat-${seatId || `${seat?.device_label || 'device'}-${seat?.user?.id || 'unknown'}-${seat?.expires_at || 'active'}`}`}
                                            >
                                                <div className="seat-device-head">
                                                    <div className={`seat-device-icon ${family.key}`}>
                                                        <SeatDeviceIcon familyKey={family.key} />
                                                    </div>
                                                    <div className="seat-device-labels">
                                                        <div className="device-family">{family.label}</div>
                                                        <div className="device-name">{seat?.device_label || 'Desktop device'}</div>
                                                    </div>
                                                    <div className={`seat-device-chip ${hasExpiration && isExpired ? 'expired' : ''}`}>
                                                        {hasExpiration ? (isExpired ? 'Expired' : formatSeatCountdown(remainingMs)) : 'No timer'}
                                                    </div>
                                                </div>

                                                {managementScope === 'team' && (
                                                    <div className="seat-owner-block">
                                                        <div className="seat-owner-name">{getSeatDisplayName(seat)}</div>
                                                        {seat?.user?.email && (
                                                            <div className="seat-owner-email">{seat.user.email}</div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="seat-meta-grid">
                                                    <div className="seat-meta-item">
                                                        <span className="meta-label">Session started</span>
                                                        <span className="meta-value">{sessionStart || 'Unavailable'}</span>
                                                    </div>
                                                    <div className="seat-meta-item">
                                                        <span className="meta-label">Last activity</span>
                                                        <span className="meta-value">{lastUsed || 'Unavailable'}</span>
                                                    </div>
                                                    <div className="seat-meta-item">
                                                        <span className="meta-label">Expires at</span>
                                                        <span className="meta-value">{expiresAt || 'Unavailable'}</span>
                                                    </div>
                                                </div>

                                                {hasExpiration && (
                                                    <div className="seat-expiration-block">
                                                        <div className="seat-expiration-head">
                                                            <span>{isExpired ? 'Seat expired' : 'Time remaining'}</span>
                                                            <span>{formatSeatCountdown(remainingMs)}</span>
                                                        </div>
                                                        <div className="seat-expiration-track">
                                                            <div
                                                                className={`seat-expiration-fill ${isExpired ? 'expired' : ''}`}
                                                                style={{ width: `${countdownPercent}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                )}

                                                <button
                                                    type="button"
                                                    className="release-seat-btn"
                                                    disabled={!desktopSeats.canRevokeSeats || !seatId || isRevoking}
                                                    onClick={() => handleRevokeSeat(seat)}
                                                >
                                                    {isRevoking ? 'Releasing...' : 'Release seat'}
                                                </button>
                                            </article>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    )}
                </div>
            </div>
            {showPortalContinueModal && portalContinuation && (
                <div className="portal-continue-dialog-backdrop" role="presentation">
                    <section className="portal-continue-dialog" role="dialog" aria-modal="true" aria-labelledby="portal-continue-title">
                        <h2 id="portal-continue-title">A desktop seat is now available</h2>
                        <p>Continue directly to Portal with {portalProviderLabel}. You will not need to sign in again.</p>
                        <div className="portal-continue-dialog-actions">
                            <button type="button" className="release-seat-btn" onClick={handleContinuePortalLogin} disabled={continuingPortalLogin}>
                                {continuingPortalLogin ? 'Continuing...' : `Continue with ${portalProviderLabel}`}
                            </button>
                            <button type="button" className="manage-seats-inline-btn" onClick={() => setShowPortalContinueModal(false)} disabled={continuingPortalLogin}>
                                Not now
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default ManageSeats;
